import { Pages } from "@typeblind/pages-shared";
import {
  acceptAll,
  adsEnabled,
  getConsent,
  onOpenConsentSettings,
  rejectAll,
  setConsent,
} from "@typeblind/thirdparties";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Link as RouterLink } from "react-router";
import * as styles from "./CookieConsent.module.less";

/**
 * Our own cookie-consent banner (replaces Cookiebot). It records the decision
 * via the consent store, which persists a first-party cookie and notifies the
 * ads/analytics bundle to load only the granted scripts. Shown on first visit
 * (no decision yet) and re-openable from the footer "Cookie settings" link.
 */
export function CookieConsent() {
  const { formatMessage } = useIntl();
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [ads, setAds] = useState(false);

  useEffect(() => {
    const current = getConsent();
    if (current == null) {
      setOpen(true);
    }
    return onOpenConsentSettings(() => {
      const saved = getConsent();
      setAnalytics(saved?.analytics ?? false);
      setAds(saved?.ads ?? false);
      setDetails(true);
      setOpen(true);
    });
  }, []);

  if (!open) {
    return null;
  }

  const close = () => setOpen(false);

  return (
    <div
      className={styles.root}
      role="dialog"
      aria-modal="false"
      aria-label={formatMessage({
        id: "consent.aria",
        defaultMessage: "Cookie consent",
      })}
    >
      <div className={styles.card}>
        <div className={styles.body}>
          <h2 className={styles.title}>
            {formatMessage({
              id: "consent.title",
              defaultMessage: "We value your privacy",
            })}
          </h2>
          <p className={styles.text}>
            {adsEnabled
              ? formatMessage({
                  id: "consent.text",
                  defaultMessage:
                    "We use cookies to keep the site running, and — with your permission — to measure traffic and show ads. You can accept all, reject non-essential cookies, or choose what to allow.",
                })
              : formatMessage({
                  id: "consent.text.noAds",
                  defaultMessage:
                    "We use cookies to keep the site running, and — with your permission — to measure traffic. You can accept all, reject non-essential cookies, or choose what to allow.",
                })}{" "}
            <RouterLink to={Pages.privacyPolicy.path} className={styles.link}>
              {formatMessage({
                id: "consent.privacyLink",
                defaultMessage: "Privacy Policy",
              })}
            </RouterLink>
          </p>

          {details && (
            <div className={styles.options}>
              <label className={`${styles.option} ${styles.optionLocked}`}>
                <input
                  type="checkbox"
                  checked={true}
                  disabled={true}
                  readOnly={true}
                />
                <span className={styles.optionText}>
                  <span className={styles.optionName}>
                    {formatMessage({
                      id: "consent.cat.necessary.name",
                      defaultMessage: "Strictly necessary",
                    })}
                  </span>
                  <span className={styles.optionDesc}>
                    {formatMessage({
                      id: "consent.cat.necessary.desc",
                      defaultMessage:
                        "Required for sign-in, your settings and security. Always on.",
                    })}
                  </span>
                </span>
              </label>

              <label className={styles.option}>
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                />
                <span className={styles.optionText}>
                  <span className={styles.optionName}>
                    {formatMessage({
                      id: "consent.cat.analytics.name",
                      defaultMessage: "Analytics",
                    })}
                  </span>
                  <span className={styles.optionDesc}>
                    {formatMessage({
                      id: "consent.cat.analytics.desc",
                      defaultMessage:
                        "Anonymous usage statistics that help us improve the app.",
                    })}
                  </span>
                </span>
              </label>

              {adsEnabled && (
                <label className={styles.option}>
                  <input
                    type="checkbox"
                    checked={ads}
                    onChange={(e) => setAds(e.target.checked)}
                  />
                  <span className={styles.optionText}>
                    <span className={styles.optionName}>
                      {formatMessage({
                        id: "consent.cat.ads.name",
                        defaultMessage: "Advertising",
                      })}
                    </span>
                    <span className={styles.optionDesc}>
                      {formatMessage({
                        id: "consent.cat.ads.desc",
                        defaultMessage:
                          "Lets our partners show ads. Premium accounts have no ads at all.",
                      })}
                    </span>
                  </span>
                </label>
              )}
            </div>
          )}
        </div>

        <div className={styles.actions}>
          {details ? (
            <button
              type="button"
              className={styles.buttonPrimary}
              onClick={() => {
                setConsent({ analytics, ads: adsEnabled && ads });
                close();
              }}
            >
              {formatMessage({
                id: "consent.save",
                defaultMessage: "Save choices",
              })}
            </button>
          ) : (
            <button
              type="button"
              className={styles.buttonGhost}
              onClick={() => setDetails(true)}
            >
              {formatMessage({
                id: "consent.customize",
                defaultMessage: "Customize",
              })}
            </button>
          )}
          <button
            type="button"
            className={styles.buttonGhost}
            onClick={() => {
              rejectAll();
              close();
            }}
          >
            {formatMessage({
              id: "consent.reject",
              defaultMessage: "Reject non-essential",
            })}
          </button>
          <button
            type="button"
            className={styles.buttonPrimary}
            onClick={() => {
              acceptAll();
              close();
            }}
          >
            {formatMessage({
              id: "consent.accept",
              defaultMessage: "Accept all",
            })}
          </button>
        </div>
      </div>
    </div>
  );
}
